export enum Environment {
  LOCAL = "local",
  DEV = "dev",
  PRODUCTION = "production",
  RRA_QA = "rraqa",
  RRA_PRODUCTION = "rra-production",
}

export type EnvironmentType =
  | "local"
  | "dev"
  | "production"
  | "rraqa"
  | "rra-production";

// Define feature flag keys here to avoid circular dependency
export enum FeatureFlagKey {
  FOUR_POINT_SCORING = "four-point-scoring",
  PUBLIC_SHAREABLE_PROFILE = "public-shareable-profile",
  PPT_EXPORT_RRA = "ppt-export-rra",
  LEADERSHIP_SPAN = "leadership-span",
  PROJECT_KANBAN_LABELS = "project-kanban-labels",
}

// Feature definition - defines what a feature is
export interface FeatureDefinition {
  key: FeatureFlagKey;
  name: string;
  description: string;
  enabledFor: {
    environments?: EnvironmentType[];
    accounts?: number[];
  };
  metadata?: Record<string, any>;
}

// Account-specific overrides
export interface AccountOverride {
  accountId: number;
  enabled: boolean;
  reason?: string;
  expiresAt?: Date;
}

// Runtime feature flag state
export interface FeatureFlag {
  key: FeatureFlagKey;
  enabled: boolean;
  description?: string;
  environments?: EnvironmentType[];
  userGroups?: string[];
  userIds?: string[];
  metadata?: Record<string, any>;
  accountOverrides?: AccountOverride[];
}

export interface FeatureFlagConfig {
  environment: EnvironmentType;
  flags: Partial<Record<FeatureFlagKey, FeatureFlag>>;
  defaultFlags?: Partial<Record<FeatureFlagKey, boolean>>;
}

export interface FeatureFlagContextType {
  isEnabled: (flagKey: FeatureFlagKey) => boolean;
  getFlag: (flagKey: FeatureFlagKey) => FeatureFlag | undefined;
  getAllFlags: () => Partial<Record<FeatureFlagKey, FeatureFlag>>;
  refreshFlags: () => Promise<void>;
  updateOverrideFlags: (
    overrideFlags: Partial<Record<FeatureFlagKey, FeatureFlag>>
  ) => void;
  isLoading: boolean;
  error: string | null;
}

export interface FeatureFlagService {
  getFlags(): Promise<Partial<Record<FeatureFlagKey, FeatureFlag>>>;
  refreshFlags(): Promise<void>;
  isEnabled(flagKey: FeatureFlagKey): boolean;
  getFlag(flagKey: FeatureFlagKey): FeatureFlag | undefined;
}

export interface FeatureFlagProviderProps {
  children: React.ReactNode;
  service?: FeatureFlagService;
  fallbackFlags?: Record<string, boolean>;
  overrideFlags?: Partial<Record<FeatureFlagKey, FeatureFlag>>;
  accountId?: number;
}
